import { Button } from "@/components/core/buttons/button";
import { ProjectForm } from "@/components/projects/project-form";
import { useProjectCreateMutation } from "@/features/dashboard/api/queries/project-query";
import type { Project } from "@/features/dashboard/types/project";
import * as Dialog from "@radix-ui/react-dialog";
import { IconX } from "@tabler/icons-react";
import { useState } from "react";
import { toast } from "sonner";

interface CreateProjectModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

export function CreateProjectModal({ isOpen, onClose, onSuccess }: CreateProjectModalProps) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const createMutation = useProjectCreateMutation();

	const handleSubmit = async (values: Project) => {
		setIsSubmitting(true);
		try {
			await createMutation.mutateAsync(values);
			toast.success("Project created successfully");
			onSuccess?.();
			onClose();
		} catch (error) {
			console.log(error);
			toast.error("Failed to create project");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={onClose}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-4xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 overflow-y-auto rounded-lg">
					<div className="flex items-center justify-between">
						<Dialog.Title className="text-xl font-semibold">Create New Project</Dialog.Title>
						<Dialog.Close asChild>
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
								<IconX className="h-4 w-4" />
							</Button>
						</Dialog.Close>
					</div>
					<div className="py-4">
						<ProjectForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
					</div>
					<div className="flex justify-end gap-2">
						<Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
							Cancel
						</Button>
						<Button
							type="submit"
							form="project-form"
							disabled={isSubmitting}
							onClick={() => {
								const form = document.querySelector("#project-form") as HTMLFormElement;
								if (form) {
									form.requestSubmit();
								}
							}}
						>
							{isSubmitting ? "Creating..." : "Create Project"}
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
